// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLinesList, { CartLinesListStyles } from "@sc/Overrides/Components/CartLinesList";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    const { enableVat, vatPriceDisplay } = getSettingsCollection(state).productSettings;
    return {
        cart,
        enableVat,
        vatPriceDisplay,
        // XNG-Change
        settingsCollection: getSettingsCollection(state),
        promotionsDataView: getPromotionsDataView(state, state.pages.onePageCheckoutConfirmation.cartId),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationProductListStyles {
    listContainer?: InjectableCss;
    itemContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productDescriptionText?: TypographyPresentationProps;
    productErpNumberText?: TypographyPresentationProps;
    qtyOrderedText?: TypographyPresentationProps;
    productPrice?: ProductPriceStyles;
    subtotalGridItem?: GridItemProps;
    subtotalText?: TypographyPresentationProps;
    // XNG-Change
    orderLinesList?: CartLinesListStyles;
    centeringWrapper?: InjectableCss;
}

export const productListStyles: OnePageCheckoutConfirmationProductListStyles = {
    productImageGridItem: { width: 2 },
    productInfoGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
        `,
    },
    qtyOrderedText: { size: 13, transform: "capitalize" },
    productPrice: {
        wrapper: {
            css: css`
                flex-direction: row;
                align-items: end;
            `,
        },
        priceLabelText: {
            variant: "p",
            size: 13,
            css: css`
                margin-right: 5px;
                margin-bottom: 0;
            `,
        },
        price: {
            priceText: { size: 13, weight: "normal" },
            unitOfMeasure: {
                unitOfMeasureText: { size: 13 },
            },
        },
    },
    subtotalGridItem: {
        width: 2,
        css: css`
            justify-content: end;
        `,
    },
};

const styles = productListStyles;

const OnePageCheckoutConfirmationProductList = ({ cart, enableVat, vatPriceDisplay, settingsCollection, promotionsDataView }: Props) => {
    if (!cart || !promotionsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }
    const { productSettings } = settingsCollection;

    return (
        <CartLinesList
            cart={cart}
            promotions={promotionsDataView.value}
            isCondensed={false}
            editable={false}
            showSavingsAmount={productSettings.showSavingsAmount}
            showSavingsPercent={productSettings.showSavingsPercent}
            extendedStyles={styles.orderLinesList}
            // XNG-Change
            hideCondensedSelector={true}
            showInventoryAvailability={false}
            showEditCartIcon={false}
            hideRemoveSection={true}
        />
    );

};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationProductList),
    definition: {
        displayName: "Product List",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
